import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Tabs, Tab, Typography, Chip, Grid, Stack, Button, Divider,
  Card, CardActionArea
} from "@mui/material";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  Building2, Stethoscope, Microscope, Radiation, Pill, Leaf, Keyboard, Brain, LayoutTemplate,
  Heart
} from "lucide-react";

// Import CHC workbench dashboards. These components render role-specific
// workbenches for each CHC staff role. They are defined in
// src/pages/chc and exported from index.js.
import {
  MODashboard,
  LabDashboard,
  RadiologyDashboard,
  NursingDashboard,
  PharmacyDashboard
} from '../chc';

/** All possible tab keys (we'll filter dynamically by role + centre) */
const ALL_TABS = [
  { key: "centres",   label: "Health Centre Cards",  icon: <Building2 size={18} /> },
  { key: "mo",        label: "Medical Officer",      icon: <Stethoscope size={18} /> },
  { key: "lab",       label: "Laboratory",           icon: <Microscope size={18} /> },
  { key: "radiology", label: "Radiology",            icon: <Radiation size={18} /> },
  { key: "pharmacy",  label: "Pharmacy",             icon: <Pill size={18} /> },
  { key: "nursing",   label: "Nursing",              icon: <Heart size={18} /> },
  { key: "ayush",     label: "AYUSH OPD",            icon: <Leaf size={18} /> },
  { key: "deo",       label: "DEO Workbench",        icon: <Keyboard size={18} /> },
  { key: "psy",       label: "Psychiatry",           icon: <Brain size={18} /> },
];

/** Backend fetch with safe fallback (NOTE: Dhundhara spelling + Radiology=TRUE) */
async function tryFetchCentres() {
  try {
    const res = await fetch("/api/centres");
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length) return data;
    }
  } catch (_) {}
  return [
    { code: "CRHA_DHAWA",     name: "CRHA Dhawa",        type: "CRHA",
      services: { lab: true,  radiology: true,  pharmacy: true,  ayush: false, psy: false } },
    { code: "CRHA_DUNDHARA",  name: "CRHA Dhundhara",    type: "CRHA",
      services: { lab: true,  radiology: true,  pharmacy: true,  ayush: false, psy: true  } },
    { code: "CRHA_KERU",      name: "CRHA Keru",         type: "CRHA",
      services: { lab: true,  radiology: true,  pharmacy: true,  ayush: false, psy: false } },
    { code: "UHTC_PN",        name: "UHTC Pratap Nagar", type: "UHTC",
      services: { lab: true,  radiology: true,  pharmacy: true,  ayush: true,  psy: false } },
  ];
}

/** Small helper to show centre chip (read-only) when ?centre= is present */
function CentreChip({ centres, centreCode }) {
  if (!centreCode) return null;
  const c = centres.find(x => x.code === centreCode);
  return <Chip size="small" sx={{ mb: 2 }} label={`Centre: ${c ? c.name : centreCode}`} />;
}

function TabPanel({ children, activeKey, myKey }) {
  return (
    <div role="tabpanel" hidden={activeKey !== myKey} aria-labelledby={`tab-${myKey}`}>
      {activeKey === myKey && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function CHCCoordination() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // centre comes from the URL (deep link), no dropdown/buttons anywhere
  const centre = searchParams.get("centre") || "";

  // user/role for role-scoped tabs + centre scoping
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('loggedInUser') || 'null') || {}; }
    catch { return {}; }
  }, []);
  const role = String(user?.role || user?.userType || "").toLowerCase();
  const assignedCentre = user?.assignedCentreCode || user?.centreCode || user?.centre || "";

  const [centres, setCentres] = useState([]);
  useEffect(() => { tryFetchCentres().then(setCentres); }, []);

  // force an MO to their own centre context when opening non-centre tabs
  useEffect(() => {
    const isMO = role.includes('medical officer');
    if (isMO && assignedCentre) {
      const next = new URLSearchParams(searchParams);
      if (next.get('centre') !== assignedCentre) {
        next.set('centre', assignedCentre);
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [role, assignedCentre]);

  const visibleCentres = useMemo(() => {
    const isMO = role.includes('medical officer');
    if (isMO && assignedCentre) return centres.filter(c => c.code === assignedCentre);
    return centres;
  }, [centres, role, assignedCentre]);

  // tabKey from URL with default
  const initialKey = (searchParams.get("tab") || "centres").toLowerCase();

  // Build dynamic tabs based on ROLE and CENTRE
  const dynamicTabs = useMemo(() => {
    const allowAyush = centre === "UHTC_PN";
    const allowPsy   = centre === "CRHA_DUNDHARA";
    let tabs = ALL_TABS.filter(t => {
      if (t.key === "ayush" && !allowAyush) return false;
      if (t.key === "psy"   && !allowPsy)   return false;
      return true;
    });

    // role-based visibility
    const r = role;
    const isMOorCMFM = ["admin","faculty","senior resident","junior resident","intern","cmfm","medical officer"]
      .some(x => r.includes(x));
    if (isMOorCMFM) return tabs;

    if (r.includes("deo"))            return tabs.filter(t => ["centres","deo"].includes(t.key));
    if (r.includes("lab technician")) return tabs.filter(t => ["centres","lab"].includes(t.key));
    if (r.includes("radiographer"))   return tabs.filter(t => ["centres","radiology"].includes(t.key));
    if (r.includes("pharmacist"))     return tabs.filter(t => ["centres","pharmacy"].includes(t.key));
    if (r.includes("nursing officer")) return tabs.filter(t => ["centres","nursing"].includes(t.key));

    return tabs.filter(t => t.key === "centres");
  }, [role, centre]);

  const [tabKey, setTabKey] = useState(
    dynamicTabs.some(t => t.key === initialKey) ? initialKey : dynamicTabs[0]?.key || "centres"
  );
  useEffect(() => {
    if (!dynamicTabs.some(t => t.key === tabKey)) {
      setTabKey(dynamicTabs[0]?.key || "centres");
    }
  }, [dynamicTabs, tabKey]);

  // keep URL in sync (no reload)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tabKey);
    if (centre) next.set("centre", centre); else next.delete("centre");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [tabKey, centre]);

  const go = (nextKey, centreCode) => {
    const p = new URLSearchParams();
    p.set("tab", nextKey);
    if (centreCode) p.set("centre", centreCode);
    navigate(`/dashboard/chc-coordination?${p.toString()}`, { replace: false });
  };

  const ServiceCard = ({ label, icon, disabled, onClick }) => (
    <Card elevation={0}
      sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider',
            bgcolor: disabled ? 'action.disabledBackground' : 'transparent', opacity: disabled ? 0.6 : 1 }}>
      <CardActionArea disabled={disabled} onClick={onClick} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <span className="text-orange-400">{icon}</span>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );

  return (
    <div className="p-6 min-h-screen rounded-xl shadow border transition-colors"
         style={{ background: 'var(--mui-palette-background-default)', borderColor: 'var(--mui-palette-divider)' }}>
      <Typography variant="h6" className="font-semibold mb-1" sx={{ color: 'var(--mui-palette-warning-300)' }}>
        Centre Services Hub / स्वास्थ्य केंद्र सेवाएँ हब
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--mui-palette-text-secondary)', mb: 3 }}>
        DEO registers → MO orders → Lab/Radiology upload → MO prescribes → Pharmacy.
      </Typography>

      <Box sx={{ borderRadius: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={Math.max(0, dynamicTabs.findIndex(t => t.key === tabKey))}
          onChange={(_e, i) => setTabKey(dynamicTabs[i]?.key || "centres")}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Centre Services Hub Tabs"
        >
          {dynamicTabs.map(t => (
            <Tab key={t.key} id={`tab-${t.key}`} aria-controls={`tabpanel-${t.key}`}
                 icon={t.icon} iconPosition="start" label={t.label} sx={{ textTransform: "none" }} />
          ))}
        </Tabs>

        <Divider />

        {/* ---- Centres tab ---- */}
        <TabPanel activeKey={tabKey} myKey="centres">
          <Grid container spacing={2}>
            {visibleCentres.map((c) => (
              <Grid key={c.code} item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">{c.name}</Typography>
                    <Chip size="small" label={c.type} />
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={1.2}>
                    <Grid item xs={6} sm={4}>
                      <ServiceCard label="MO Workbench" icon={<Stethoscope size={16} />} onClick={() => go("mo", c.code)} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <ServiceCard label="Laboratory" icon={<Microscope size={16} />} onClick={() => go("lab", c.code)} disabled={!c.services?.lab} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <ServiceCard label="Radiology" icon={<Radiation size={16} />} onClick={() => go("radiology", c.code)} disabled={!c.services?.radiology} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <ServiceCard label="Pharmacy" icon={<Pill size={16} />} onClick={() => go("pharmacy", c.code)} disabled={!c.services?.pharmacy} />
                    </Grid>
                    {c.code === "UHTC_PN" && (
                      <Grid item xs={6} sm={4}>
                        <ServiceCard label="AYUSH OPD" icon={<Leaf size={16} />} onClick={() => go("ayush", c.code)} />
                      </Grid>
                    )}
                    <Grid item xs={6} sm={4}>
                      <ServiceCard label="DEO Workbench" icon={<Keyboard size={16} />} onClick={() => go("deo", c.code)} />
                    </Grid>
                    {c.code === "CRHA_DUNDHARA" && (
                      <Grid item xs={6} sm={4}>
                        <ServiceCard label="Psychiatry" icon={<Brain size={16} />} onClick={() => go("psy", c.code)} />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button variant="outlined"
                              onClick={() => navigate(`/dashboard/centre/${encodeURIComponent(c.code)}`)}
                              startIcon={<LayoutTemplate size={16} />}>
                        Open Centre Board
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* ---- Workbench tabs ---- */}
        <TabPanel activeKey={tabKey} myKey="mo">
          <CentreChip centres={centres} centreCode={centre} />
          <MODashboard />
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="lab">
          <CentreChip centres={centres} centreCode={centre} />
          <LabDashboard />
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="radiology">
          <CentreChip centres={centres} centreCode={centre} />
          <RadiologyDashboard />
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="pharmacy">
          <CentreChip centres={centres} centreCode={centre} />
          <PharmacyDashboard />
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="nursing">
          <CentreChip centres={centres} centreCode={centre} />
          <NursingDashboard />
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="ayush">
          <CentreChip centres={centres} centreCode={centre} />
          <Typography variant="subtitle1" className="mb-2">AYUSH OPD</Typography>
          <Typography variant="body2" className="mb-3">AYUSH diagnoses/procedures; shares citizen, orders, results, documents.</Typography>
          <Stack direction="row" spacing={1}><Button component={Link} to="/citizen-management" variant="outlined">Open Citizen</Button></Stack>
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="deo">
          <CentreChip centres={centres} centreCode={centre} />
          <Typography variant="subtitle1" className="mb-2">DEO Workbench</Typography>
          <Typography variant="body2" className="mb-3">Start from OPD slip, upload/link docs, transcribe external lab/X-ray, close visit.</Typography>
          <Stack direction="row" spacing={1}><Button component={Link} to="/citizen-management" variant="outlined">Open Citizen</Button></Stack>
        </TabPanel>

        <TabPanel activeKey={tabKey} myKey="psy">
          <CentreChip centres={centres} centreCode={centre} />
          <Typography variant="subtitle1" className="mb-2">Psychiatry (CRHA Dhundhara)</Typography>
          <Typography variant="body2" className="mb-3">Psychiatry follow-ups & referrals; shares investigations and prescriptions.</Typography>
          <Stack direction="row" spacing={1}><Button component={Link} to="/citizen-management" variant="outlined">Open Citizen</Button></Stack>
        </TabPanel>
      </Box>
    </div>
  );
}
